import { createApi } from '@reduxjs/toolkit/query/react'
import { sendNotification } from 'components'
import axiosBaseQuery from 'services/axiosBaseQuery'
import { GenericResponse, TableRequestV2, TableResponseV2 } from 'services/types'
import {
  CreateNormalizationFactorRequest,
  MutationResponse,
  NormalizationFactorDetailResponse,
} from './types'
import { HistoryRequest, HistoryResponse } from 'containers/HistoryV2/types'

export const normalizationApi = createApi({
  reducerPath: 'normalizationApi',
  tagTypes: ['NormalizationFactors', 'NormalizationFactorDetail', 'NormalizationHistory'],
  baseQuery: axiosBaseQuery({
    baseUrl: `${window.envVariables.REACT_APP_BASE_URL + '/MRF/'}`,
  }),
  endpoints: (builder) => ({
    getNormalizationList: builder.query<TableResponseV2<{}>, TableRequestV2<number>>({
      query: ({ client_id, ...data }) => ({
        url: `/clients/${client_id}/normalization_factor/list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['NormalizationFactors'],
    }),
    deleteNormalizationFactor: builder.mutation<
      GenericResponse,
      { deleteId: string; clientId: number }
    >({
      query: ({ clientId, deleteId }) => ({
        url: `clients/${clientId}/normalization_factor/${deleteId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['NormalizationFactors'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    createNormalizationFactor: builder.mutation<GenericResponse, CreateNormalizationFactorRequest>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/normalization_factor/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['NormalizationFactors'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    getNormalizationDetail: builder.query<
      NormalizationFactorDetailResponse & GenericResponse,
      { clientId: number; id: number }
    >({
      query: ({ clientId, id }) => ({
        url: `clients/${clientId}/normalization_factor/${id}/`,
        method: 'GET',
      }),
      providesTags: ['NormalizationFactorDetail'],
    }),
    editNormalizationFactor: builder.mutation<
      GenericResponse,
      CreateNormalizationFactorRequest & { id: number }
    >({
      query: ({ clientId, id, ...data }) => ({
        url: `clients/${clientId}/normalization_factor/${id}/`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: [
        'NormalizationFactors',
        'NormalizationFactorDetail',
        'NormalizationHistory',
      ],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    normalizationHistory: builder.query<
      HistoryResponse & MutationResponse,
      HistoryRequest & { clientId: number; id: number }
    >({
      query({ clientId, id, ...data }) {
        return {
          url: `clients/${clientId}/normalization_factor/${id}/history/`,
          method: 'POST',
          data,
        }
      },
      providesTags: ['NormalizationHistory'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
  }),
})

export const {
  useGetNormalizationListQuery,
  useDeleteNormalizationFactorMutation,
  useCreateNormalizationFactorMutation,
  useGetNormalizationDetailQuery,
  useEditNormalizationFactorMutation,
  useNormalizationHistoryQuery,
} = normalizationApi
